import React, { useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input, Label, Textarea } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Plus, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PILLARS = ["Environment", "Social", "Governance"];
const TYPES = ["number", "percentage", "boolean", "text"];
const FREQ = ["Monthly", "Quarterly", "Yearly"];

export default function AttributesPanel({ attributes, canEdit, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [toggleAttr, setToggleAttr] = useState(null);
  const [reason, setReason] = useState("");
  const [form, setForm] = useState({ name: "", data_type: "number", unit: "", why: "", pillar: "Environment", data_source: "", owner_role: "", frequency: "Monthly", widget: false, reason: "" });

  const add = async () => {
    try {
      await api.post("/valueprint/me/attributes", form);
      toast.success("Custom attribute added — logged in the Override Log");
      setAddOpen(false);
      setForm({ name: "", data_type: "number", unit: "", why: "", pillar: "Environment", data_source: "", owner_role: "", frequency: "Monthly", widget: false, reason: "" });
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggle = async () => {
    try {
      const { data } = await api.post(`/valueprint/me/attributes/${toggleAttr.key}/toggle`, { reason });
      toast.success(data.enabled ? "Attribute re-enabled" : "Attribute disabled — logged in the Override Log");
      setToggleAttr(null); setReason("");
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-3" data-testid="vp-attributes-panel">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Unique tenant attributes — {attributes.filter((a) => a.enabled !== false).length} active / {attributes.length} total</div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="vp-add-attribute-btn">
            <Plus className="h-3 w-3" /> Add custom attribute
          </Button>
        )}
      </div>
      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Attribute</TableHead><TableHead>Type / Unit</TableHead><TableHead>Pillar</TableHead>
              <TableHead>Source template</TableHead><TableHead>Data owner</TableHead><TableHead>Frequency</TableHead>
              <TableHead>Widget</TableHead><TableHead>Status</TableHead>{canEdit && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {attributes.map((a) => (
              <TableRow key={a.key} className={a.enabled === false ? "opacity-50" : ""} data-testid={`vp-attr-row-${a.key}`}>
                <TableCell>
                  <div className="text-sm font-medium">{a.name}</div>
                  {a.why && <div className="text-[10px] text-muted-foreground max-w-xs line-clamp-2">{a.why}</div>}
                </TableCell>
                <TableCell className="font-mono text-xs">{a.data_type}{a.unit ? ` · ${a.unit}` : ""}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{a.pillar || "—"}</Badge></TableCell>
                <TableCell className="font-mono text-[10px]">{a.source === "custom" ? <Badge variant="lime" className="text-[9px]">custom</Badge> : a.source}</TableCell>
                <TableCell className="text-xs">{a.owner_role || "—"}</TableCell>
                <TableCell className="text-xs">{a.frequency || "—"}</TableCell>
                <TableCell>{a.widget ? <Badge variant="success" className="text-[9px]">widget</Badge> : "—"}</TableCell>
                <TableCell>
                  {a.enabled === false
                    ? <Badge variant="danger" className="text-[9px]" title={a.disabled_reason || ""}>disabled</Badge>
                    : <Badge variant="outline" className="text-[9px]">active</Badge>}
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setToggleAttr(a); setReason(""); }} data-testid={`vp-attr-toggle-${a.key}`}>
                      {a.enabled === false ? <><RotateCcw className="h-3 w-3" /> Enable</> : <><Ban className="h-3 w-3" /> Disable</>}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add custom attribute</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Vertical greenery installed" data-testid="vp-attr-name-input" /></div>
            <div><Label>Data type</Label>
              <Select value={form.data_type} onValueChange={(v) => setForm({ ...form, data_type: v })}>
                <SelectTrigger data-testid="vp-attr-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="m2, %, kWh…" data-testid="vp-attr-unit-input" /></div>
            <div><Label>Pillar</Label>
              <Select value={form.pillar} onValueChange={(v) => setForm({ ...form, pillar: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PILLARS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Collection frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Data owner (role)</Label><Input value={form.owner_role} onChange={(e) => setForm({ ...form, owner_role: e.target.value })} placeholder="e.g. Design Lead" /></div>
            <div><Label>Data source</Label><Input value={form.data_source} onChange={(e) => setForm({ ...form, data_source: e.target.value })} placeholder="e.g. Project records" /></div>
            <div className="col-span-2"><Label>Why it matters</Label><Textarea rows={2} value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.widget} onChange={(e) => setForm({ ...form, widget: e.target.checked })} className="accent-[hsl(var(--accent))]" data-testid="vp-attr-widget-check" />
              Show as a Control Tower widget
            </label>
            <div className="col-span-2"><Label>Reason (for the Override Log) *</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why does your business need this attribute?" data-testid="vp-attr-reason-input" /></div>
          </div>
          <Button variant="primary" onClick={add} disabled={!form.name.trim() || form.reason.trim().length < 5} data-testid="vp-attr-create-btn">Add attribute</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toggleAttr} onOpenChange={(o) => !o && setToggleAttr(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{toggleAttr?.enabled === false ? "Re-enable" : "Disable"} “{toggleAttr?.name}”</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Templates never silently delete — this change is recorded in the Template Override Log with your reason.</p>
          <div><Label>Reason *</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Not applicable — we do not operate this activity" data-testid="vp-attr-toggle-reason" /></div>
          <Button variant="primary" onClick={toggle} disabled={reason.trim().length < 5} data-testid="vp-attr-toggle-confirm">Confirm &amp; log override</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
